import { HeartHandshake } from "lucide-react";
import { useCanAccess, useNotify, type Identifier } from "ra-core";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  maintenanceSummary,
  resultLine,
  sellsPlan,
  type WelcomeCall,
} from "./welcome";
import { WelcomeCallDialog } from "./WelcomeCallDialog";
import { takeWelcomeCall, useClientWelcomeCalls } from "./useWelcomeCalls";

const STATUS_LABEL = {
  pending: "Pendiente",
  done: "Hecha",
  skipped: "Omitida",
} as const;

/** The welcome call of a client, on the sheet: to make, or what they said. */
export const ClientWelcome = ({
  contactId,
  onChanged,
}: {
  contactId: Identifier;
  onChanged?: () => void;
}) => {
  const notify = useNotify();
  const { data: all = [], refetch } = useClientWelcomeCalls(contactId);
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "team",
    action: "list",
  });
  const [open, setOpen] = useState<WelcomeCall | null>(null);
  // Welcome-only calls (no plan to sell) are for the admins (5 nov)
  const calls = isAdmin ? all : all.filter(sellsPlan);
  if (!calls.length) return null;

  const start = async (call: WelcomeCall) => {
    try {
      if (call.status === "pending" && call.assigned_to == null)
        await takeWelcomeCall(call.id);
      setOpen(call);
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <h4 className="flex items-center gap-2 text-sm font-semibold">
        <HeartHandshake className="h-4 w-4" />
        Llamada de bienvenida
      </h4>
      {calls.map((call) => (
        <div
          key={call.id}
          className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm"
        >
          <div className="flex min-w-0 flex-col">
            <span className="flex items-center gap-2">
              <Badge
                variant={call.status === "pending" ? "default" : "secondary"}
              >
                {STATUS_LABEL[call.status]}
              </Badge>
              {call.status === "done" && call.done_by_name ? (
                <span className="text-muted-foreground">
                  {call.done_by_name.split(" ")[0]}
                  {call.done_at
                    ? ` · ${new Date(call.done_at).toLocaleDateString("es-ES")}`
                    : ""}
                </span>
              ) : null}
              {call.status === "pending" && call.attempts ? (
                <span className="text-muted-foreground">
                  {call.attempts} sin contestar
                </span>
              ) : null}
            </span>
            <span className="truncate text-muted-foreground">
              {call.status === "done"
                ? resultLine(call)
                : maintenanceSummary(call).chip}
            </span>
          </div>
          <Button
            size="sm"
            variant={call.status === "pending" ? "default" : "outline"}
            onClick={() => start(call)}
          >
            {call.status === "pending" ? "Hacer la llamada" : "Ver"}
          </Button>
        </div>
      ))}
      <WelcomeCallDialog
        call={open}
        onClose={() => setOpen(null)}
        onChanged={() => {
          refetch();
          onChanged?.();
        }}
      />
    </section>
  );
};
